// VYV shared analytics engine — pure, module-agnostic, testable.
//
// One place for the time-series math every module was re-implementing by hand
// (streak duplicated in ≥5 hooks with divergent — sometimes buggy — rules).
// Input is always a flat list of Samples {t,v,category?}; output is aggregates,
// period-over-period trend, ONE canonical streak, and a simple projection.
//
// Design rules:
// - PURE: no React, no Supabase, no module imports. `now` is injectable so
//   every function is deterministic and unit-testable.
// - LOCAL time: buckets/streaks use the device local day (via Date getters),
//   NOT UTC. This fixes the useMirrorScore streak, which bucketed on
//   `toISOString()` UTC day strings and drifted by a day for BRT users.
// - No throwing: empty input yields empty/zero results, never NaN.

export type Granularity = "day" | "week" | "month";
export type Reducer = "sum" | "avg" | "count";

/** A single data point. Use v=1 for count-only series (e.g. "a workout happened"). */
export interface Sample {
  /** epoch milliseconds */
  t: number;
  /** numeric value; ignored by the "count" reducer */
  v: number;
  /** optional grouping key (muscle_group, source, meal_type…) */
  category?: string;
}

export interface Bucket {
  /** stable label: "2026-07-15" (day/week-start) | "2026-07" (month) */
  key: string;
  /** epoch ms of the bucket's local start */
  start: number;
  /** reduced value over the bucket */
  value: number;
  /** number of samples in the bucket */
  count: number;
}

// ── Local calendar helpers ────────────────────────────────────────────────────

function startOfLocalDay(t: number): Date {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d;
}

function pad2(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

function dayKeyOf(d: Date): string {
  return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
}

function startOfLocalWeek(t: number, weekStartsOn: 0 | 1): Date {
  const d = startOfLocalDay(t);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const diff = (dow - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function startOfLocalMonth(t: number): Date {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}

/** Bucket anchor (Date) + key for a granularity. */
function anchorFor(t: number, g: Granularity, weekStartsOn: 0 | 1): { start: Date; key: string } {
  if (g === "day") {
    const s = startOfLocalDay(t);
    return { start: s, key: dayKeyOf(s) };
  }
  if (g === "week") {
    const s = startOfLocalWeek(t, weekStartsOn);
    return { start: s, key: dayKeyOf(s) };
  }
  const s = startOfLocalMonth(t);
  return { start: s, key: s.getFullYear() + "-" + pad2(s.getMonth() + 1) };
}

/** Advance a bucket-start Date by exactly one bucket (mutates a clone). */
function stepForward(start: Date, g: Granularity): Date {
  const d = new Date(start.getTime());
  if (g === "day") d.setDate(d.getDate() + 1);
  else if (g === "week") d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  return d;
}

// ── Aggregation ───────────────────────────────────────────────────────────────

export interface AggregateConfig {
  granularity: Granularity;
  reducer: Reducer;
  /** Monday (1) by default; 0 = Sunday. */
  weekStartsOn?: 0 | 1;
  /**
   * Insert zero-value buckets for empty calendar slots between the first and
   * last sample so a trend/projection sees continuous time. Defaults to true
   * for sum/count (a gap genuinely means 0) and false for avg (a 0 would
   * poison the mean — an unlogged day is "no data", not "an average of 0").
   */
  fillGaps?: boolean;
}

/**
 * Bucket samples by local day/week/month and reduce each bucket.
 * Returns buckets in ascending chronological order.
 */
export function aggregate(samples: Sample[], cfg: AggregateConfig): Bucket[] {
  const weekStartsOn = cfg.weekStartsOn ?? 1;
  const fillGaps = cfg.fillGaps ?? (cfg.reducer !== "avg");

  if (samples.length === 0) return [];

  // Group by bucket key.
  const groups = new Map<string, { start: number; sum: number; count: number }>();
  for (const s of samples) {
    if (!Number.isFinite(s.t)) continue;
    const { start, key } = anchorFor(s.t, cfg.granularity, weekStartsOn);
    const g = groups.get(key);
    const v = Number.isFinite(s.v) ? s.v : 0;
    if (g) {
      g.sum += v;
      g.count += 1;
    } else {
      groups.set(key, { start: start.getTime(), sum: v, count: 1 });
    }
  }
  if (groups.size === 0) return [];

  const reduce = (sum: number, count: number): number => {
    if (cfg.reducer === "count") return count;
    if (cfg.reducer === "avg") return count > 0 ? sum / count : 0;
    return sum;
  };

  const ordered = [...groups.entries()]
    .map(([key, g]) => ({ key, start: g.start, value: reduce(g.sum, g.count), count: g.count }))
    .sort((a, b) => a.start - b.start);

  if (!fillGaps) return ordered;

  // Walk the calendar from first to last bucket, inserting zeros for gaps.
  const out: Bucket[] = [];
  let cursor = new Date(ordered[0].start);
  const lastStart = ordered[ordered.length - 1].start;
  const byStart = new Map(ordered.map((b) => [b.start, b]));
  // Guard against pathological ranges (bad timestamps) — cap iterations.
  let guard = 0;
  while (cursor.getTime() <= lastStart && guard < 10_000) {
    const hit = byStart.get(cursor.getTime());
    if (hit) {
      out.push(hit);
    } else {
      const { key } = anchorFor(cursor.getTime(), cfg.granularity, weekStartsOn);
      out.push({ key, start: cursor.getTime(), value: 0, count: 0 });
    }
    cursor = stepForward(cursor, cfg.granularity);
    guard++;
  }
  return out;
}

// ── Period-over-period trend ──────────────────────────────────────────────────

export interface Trend {
  /** reduced value over [now - windowDays, now] */
  current: number;
  /** reduced value over the window immediately before that */
  previous: number;
  /** current - previous */
  delta: number;
  /** percent change vs previous; null when previous is 0 (undefined ratio) */
  pct: number | null;
  direction: "up" | "down" | "flat";
  /** Present when requested: an empty period differs from an observed zero. */
  currentSamples?: number;
  previousSamples?: number;
}

export interface TrendConfig {
  windowDays: number;
  reducer: Reducer;
  now?: number;
  includeObservationCounts?: boolean;
}

/** Compare the last `windowDays` against the window immediately before it. */
export function periodOverPeriod(samples: Sample[], cfg: TrendConfig): Trend {
  const now = cfg.now ?? Date.now();
  const win = cfg.windowDays * 86_400_000;
  const curStart = now - win;
  const prevStart = now - 2 * win;

  let curSum = 0, curCount = 0, prevSum = 0, prevCount = 0;
  for (const s of samples) {
    if (!Number.isFinite(s.t) || (cfg.includeObservationCounts && !Number.isFinite(s.v))) continue;
    const v = Number.isFinite(s.v) ? s.v : 0;
    if (s.t > curStart && s.t <= now) { curSum += v; curCount++; }
    else if (s.t > prevStart && s.t <= curStart) { prevSum += v; prevCount++; }
  }
  const reduce = (sum: number, count: number) =>
    cfg.reducer === "count" ? count : cfg.reducer === "avg" ? (count > 0 ? sum / count : 0) : sum;

  const current = reduce(curSum, curCount);
  const previous = reduce(prevSum, prevCount);
  const delta = current - previous;
  const pct = previous === 0 ? null : (delta / Math.abs(previous)) * 100;
  const direction = delta > 1e-9 ? "up" : delta < -1e-9 ? "down" : "flat";
  const missingComparison = cfg.includeObservationCounts && (curCount === 0 || prevCount === 0);
  return { current, previous, delta, pct: missingComparison ? null : pct,
    direction: missingComparison ? "flat" : direction,
    ...(cfg.includeObservationCounts ? { currentSamples: curCount, previousSamples: prevCount } : {}) };
}

// ── Canonical streak (replaces the 5 hand-rolled copies) ──────────────────────

export interface StreakConfig {
  now?: number;
  /**
   * How many days the most-recent active day may lag "today" and still keep the
   * streak alive. 1 (default) = today OR yesterday counts (the useFocus/
   * useCheckIn rule, the correct majority). 0 = must include today.
   */
  graceDays?: number;
}

export interface StreakResult {
  /** consecutive active local-days ending at the most recent one, IF alive. */
  current: number;
  /** longest run of consecutive active days anywhere in the history. */
  longest: number;
  /** most recent active local-day key, or null when there are no samples. */
  lastActiveDay: string | null;
}

/**
 * ONE streak definition for the whole app. A "day" is a local calendar day with
 * ≥1 sample. `current` is only non-zero when the most recent active day is
 * within `graceDays` of today (so a missed today doesn't instantly zero a long
 * streak, but a week-old streak reads as 0 — it's over).
 */
export function computeStreak(timestamps: number[], cfg: StreakConfig = {}): StreakResult {
  const now = cfg.now ?? Date.now();
  const graceDays = cfg.graceDays ?? 1;

  const dayMs = 86_400_000;
  const todayStart = startOfLocalDay(now).getTime();

  // Unique active day-starts, descending.
  const set = new Set<number>();
  for (const t of timestamps) {
    if (!Number.isFinite(t)) continue;
    set.add(startOfLocalDay(t).getTime());
  }
  if (set.size === 0) return { current: 0, longest: 0, lastActiveDay: null };
  const days = [...set].sort((a, b) => b - a);

  // Day difference in whole local days (DST-safe: re-derive day starts).
  const daysBetween = (laterStart: number, earlierStart: number): number =>
    Math.round((laterStart - earlierStart) / dayMs);

  // current: does the most recent active day qualify as "alive"?
  let current = 0;
  const gapFromToday = daysBetween(todayStart, days[0]);
  if (gapFromToday <= graceDays && gapFromToday >= 0) {
    current = 1;
    for (let i = 1; i < days.length; i++) {
      if (daysBetween(days[i - 1], days[i]) === 1) current++;
      else break;
    }
  }

  // longest: max consecutive run anywhere.
  let longest = 1;
  let run = 1;
  for (let i = 1; i < days.length; i++) {
    if (daysBetween(days[i - 1], days[i]) === 1) {
      run++;
      if (run > longest) longest = run;
    } else {
      run = 1;
    }
  }

  return { current, longest, lastActiveDay: dayKeyOf(new Date(days[0])) };
}

/**
 * 7-slot activity grid for the current local week (index 0 = weekStartsOn day).
 * Absorbs useBrain's computeWeekActivity — same primitive, one home.
 */
export function weekActivityGrid(
  timestamps: number[],
  cfg: { now?: number; weekStartsOn?: 0 | 1 } = {},
): boolean[] {
  const now = cfg.now ?? Date.now();
  const weekStartsOn = cfg.weekStartsOn ?? 1;
  const weekStart = startOfLocalWeek(now, weekStartsOn).getTime();
  const grid = [false, false, false, false, false, false, false];
  for (const t of timestamps) {
    if (!Number.isFinite(t)) continue;
    const dayStart = startOfLocalDay(t).getTime();
    const idx = Math.round((dayStart - weekStart) / 86_400_000);
    if (idx >= 0 && idx < 7) grid[idx] = true;
  }
  return grid;
}

// ── Projection (moving average + linear regression) ──────────────────────────

export type Confidence = "low" | "medium" | "high";

export interface Projection {
  /** trailing moving average aligned to the input values (same length). */
  movingAverage: number[];
  /** least-squares slope per bucket (value units / bucket). */
  slope: number;
  intercept: number;
  /** projected value for the NEXT bucket (index = values.length), clamped ≥0. */
  nextValue: number;
  /** coefficient of determination R² in [0,1]; 0 when undefined. */
  r2: number;
  /** derived from R² and sample count — how much to trust `nextValue`. */
  confidence: Confidence;
}

export interface ProjectConfig {
  /** trailing window for the moving average (default 3). */
  maWindow?: number;
}

/**
 * Fit a straight line over index→value and project one bucket ahead, plus a
 * trailing moving average for smoothing. Never throws; degenerate inputs return
 * a flat, low-confidence projection.
 */
export function project(values: number[], cfg: ProjectConfig = {}): Projection {
  const maWindow = Math.max(1, cfg.maWindow ?? 3);
  const n = values.length;

  // Moving average (trailing, shrinking window at the head).
  const movingAverage = values.map((_, i) => {
    const from = Math.max(0, i - maWindow + 1);
    let sum = 0;
    for (let j = from; j <= i; j++) sum += values[j];
    return sum / (i - from + 1);
  });

  if (n < 2) {
    return {
      movingAverage,
      slope: 0,
      intercept: n === 1 ? values[0] : 0,
      nextValue: n === 1 ? Math.max(0, values[0]) : 0,
      r2: 0,
      confidence: "low",
    };
  }

  // Least-squares over x = 0..n-1.
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumXX += i * i;
  }
  const denom = n * sumXX - sumX * sumX;
  const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  // R².
  const meanY = sumY / n;
  let ssTot = 0, ssRes = 0;
  for (let i = 0; i < n; i++) {
    const pred = slope * i + intercept;
    ssTot += (values[i] - meanY) ** 2;
    ssRes += (values[i] - pred) ** 2;
  }
  const r2 = ssTot === 0 ? 0 : Math.max(0, 1 - ssRes / ssTot);

  const nextValue = Math.max(0, slope * n + intercept);

  let confidence: Confidence = "low";
  if (r2 >= 0.6 && n >= 6) confidence = "high";
  else if (r2 >= 0.3 && n >= 4) confidence = "medium";

  return { movingAverage, slope, intercept, nextValue, r2, confidence };
}
